import React, { useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/config';
import { handleFirebaseAuthError } from '../utils/firebaseErrorHandler';
import ForgotPassword from '../components/ForgotPassword';
import {
  TextField,
  Button,
  Typography,
  Box,
  Container,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Email, Lock } from '@mui/icons-material';

const Auth: React.FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Let the App.tsx routing handle the redirection based on email verification status
    } catch (err) {
      setError(handleFirebaseAuthError(err, i18n.language));
    } finally {
      setLoading(false);
    }
  };

  const goToRegistration = () => {
    history.push('/register');
  };

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <Container maxWidth="sm" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
          <Card 
            className="glass-effect" 
            sx={{ 
              width: '100%', 
              overflow: 'visible'
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography 
                variant="h4" 
                className="animated-gradient-text" 
                align="center" 
                gutterBottom
                sx={{ 
                  position: 'relative', 
                  zIndex: 2,
                  fontFamily: 'Poppins, sans-serif'
                }}
              >
                {t('auth.login')}
              </Typography>

              <Box component="form" onSubmit={handleLogin} sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  label={t('auth.email')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: <Email sx={{ color: 'primary.main', mr: 1 }} />,
                  }}
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label={t('auth.password')}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: <Lock sx={{ color: 'primary.main', mr: 1 }} />,
                  }}
                  sx={{ mb: 3 }}
                />

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mb: 2 }}
                >
                  {loading ? <CircularProgress size={24} /> : t('auth.login')}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Button
                    variant="text"
                    onClick={() => setForgotPasswordOpen(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('auth.forgot_password')}
                  </Button>
                </Box>

                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={goToRegistration}
                  className="gradient-border"
                >
                  {t('registration.register')}
                </Button>

                <ForgotPassword 
                  open={forgotPasswordOpen} 
                  onClose={() => setForgotPasswordOpen(false)} 
                />
              </Box>
            </CardContent>
          </Card>
        </Container>
      </IonContent>
    </IonPage>
  );
};

export default Auth;
